// ═══════════════════════════════════════════════════════════════
// Debris Portal — Monitor Tab
// Road Monitor: signs tickets at loading site (draft tickets)
// Tower Monitor: scans QR at disposal, verify with 4 signatures
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useCallback } from 'react';
import { useDebrisOpsStore } from '../../../store/debrisOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../../types/debris';
import type { DebrisZone, DebrisLoadTicket } from '../../../types/debris';
import type { PortalRole } from './DebrisPortal';
import { notifications } from '../../../lib/notifications';
import { SignaturePad } from '../../../components/ui/SignaturePad';
import { IconCheck, IconX, IconSearch, IconQrcode, IconAlertTriangle } from '@tabler/icons-react';
import { QRScanner } from '../../../components/scanner/QRScanner';

interface Props {
  zone: DebrisZone;
  role: PortalRole | null;
}

function parseStops(json: string | undefined): any[] {
  try { return JSON.parse(json || '[]'); } catch { return []; }
}

// ── Road Monitor View ─────────────────────────────────────────
// Shown when role === 'road_monitor'.
// Lists draft/in-progress tickets so the monitor can sign them.

function RoadMonitorView({ zone }: { zone: DebrisZone }) {
  const { user } = useAuthStore();
  const { loadTickets, updateLoadTicket } = useDebrisOpsStore();
  const [selected, setSelected] = useState<DebrisLoadTicket | null>(null);
  const [monitorName, setMonitorName] = useState((user as any)?.email?.split('@')[0] ?? '');
  const [loadPct, setLoadPct] = useState(75);
  const [monitorSig, setMonitorSig] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const zoneTickets = useMemo(
    () => loadTickets.filter(t => t.zoneId === String(zone.id) && (t.status === 'draft' || t.status === 'in_transit')),
    [loadTickets, zone.id],
  );

  const filtered = useMemo(() => {
    if (!search) return zoneTickets;
    const q = search.toLowerCase();
    return zoneTickets.filter(t =>
      (t.ticketNumber || '').toLowerCase().includes(q) ||
      (t.truckNumber || '').toLowerCase().includes(q),
    );
  }, [zoneTickets, search]);

  const handleSign = async () => {
    if (!selected) return;
    if (!monitorName.trim()) { notifications.show({ message: 'Enter your name', color: 'var(--color-error)' }); return; }
    if (!monitorSig) { notifications.show({ message: 'Signature required', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      await updateLoadTicket(selected.id, {
        loadingMonitorName: monitorName.trim(),
        loadingMonitorSignature: monitorSig,
        loadingLoadPct: String(loadPct),
      } as any);
      notifications.show({ message: `Signed ticket ${selected.ticketNumber || `#${selected.id}`}`, color: 'var(--color-success)' });
      setSelected(null);
      setMonitorSig('');
    } catch {
      notifications.show({ message: 'Failed to save signature', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ background: '#f5f3ff', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 12, color: '#5b21b6', fontWeight: 600 }}>
        🦺 Road Monitor · {zone.zoneName || zone.zoneCode}
      </div>
      <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 16 }}>
        Tap a ticket to add your load estimate and signature at the loading site.
      </div>

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <IconSearch size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
        <input className="sp-input" style={{ paddingLeft: 32, fontSize: 14 }}
          placeholder="Ticket # or truck…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#939598', fontSize: 14, padding: '40px 0' }}>
          No active tickets in this zone.
        </div>
      )}

      {filtered.map(t => {
        const stops = parseStops(t.pickupStopsJson);
        const alreadySigned = !!t.loadingMonitorSignature;
        const color = DEBRIS_TYPE_COLORS[t.debrisType] || '#7B7D7F';
        return (
          <div key={t.id}
            onClick={() => { setSelected(t); setLoadPct(parseFloat(t.loadingLoadPct || '75') || 75); }}
            style={{
              background: alreadySigned ? '#f0fdf4' : '#fff',
              border: `1.5px solid ${alreadySigned ? '#bbf7d0' : '#e2e8f0'}`,
              borderRadius: 4, padding: '16px 16px', marginBottom: 8, cursor: 'pointer',
              borderLeft: `4px solid ${color}`,
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C' }}>
                  {t.ticketNumber || `#${t.id}`}
                  {t.truckNumber && <span style={{ fontSize: 12, color: '#7B7D7F', fontWeight: 400 }}> · Truck {t.truckNumber}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                  {DEBRIS_TYPE_LABELS[t.debrisType] || t.debrisType}
                  {stops.length > 0 && ` · ${stops.length} stop${stops.length !== 1 ? 's' : ''}`}
                </div>
              </div>
              {alreadySigned
                ? <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', background: '#dcfce7', padding: '3px 8px', borderRadius: 4 }}>✓ Signed</span>
                : <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', background: '#ede9fe', padding: '3px 8px', borderRadius: 4 }}>Needs Sig</span>
              }
            </div>
          </div>
        );
      })}

      {/* Sign drawer */}
      {selected && (
        <div className="sp-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="sp-drawer" onClick={e => e.stopPropagation()} style={{ paddingBottom: 80 }}>
            <div className="sp-drawer-handle" />
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 4 }}>
              {selected.ticketNumber || `Ticket #${selected.id}`}
            </div>
            <div style={{ fontSize: 14, color: '#7B7D7F', marginBottom: 16 }}>
              Truck {selected.truckNumber || '—'} · {DEBRIS_TYPE_LABELS[selected.debrisType] || selected.debrisType}
            </div>

            {/* Stops at loading site */}
            {(() => {
              const stops = parseStops(selected.pickupStopsJson);
              return stops.length > 0 ? (
                <div style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Pickup Stops</div>
                  {stops.map((s: any, i: number) => (
                    <div key={i} style={{ fontSize: 12, color: '#475569', padding: '3px 0' }}>
                      <span style={{ color: '#939598', marginRight: 8 }}>{i + 1}.</span>{s.address || 'No address'}
                    </div>
                  ))}
                </div>
              ) : null;
            })()}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
                Your Load Estimate: <strong>{loadPct}%</strong>
              </label>
              <input type="range" min={5} max={100} step={5} value={loadPct}
                onChange={e => setLoadPct(Number(e.target.value))} style={{ width: '100%' }} />
            </div>

            <div className="sp-field">
              <label className="sp-label">Monitor Name / ID <span className="sp-req">*</span></label>
              <input className="sp-input" value={monitorName} onChange={e => setMonitorName(e.target.value)} placeholder="Full name or badge ID" />
            </div>

            <SignaturePad
              label="Loading Monitor Signature *"
              onCapture={setMonitorSig}
              initial={selected.loadingMonitorSignature}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sp-btn sp-btn-secondary sp-btn-sm" style={{ flex: 1 }} onClick={() => setSelected(null)}>Cancel</button>
              <button
                className="sp-btn sp-btn-primary sp-btn-sm"
                style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                onClick={handleSign} disabled={saving}>
                <IconCheck size={16} /> {saving ? 'Saving…' : 'Sign Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tower Monitor View ────────────────────────────────────────
// Shown when role === 'tower_monitor' (or null/default).
// QR scan → verify with tower monitor + driver delivery signatures.

function TowerMonitorView({ zone }: { zone: DebrisZone }) {
  const { user } = useAuthStore();
  const { loadTickets, updateLoadTicket } = useDebrisOpsStore();

  const [statusFilter, setStatusFilter] = useState<'in_transit' | 'all'>('in_transit');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DebrisLoadTicket | null>(null);
  const [towerLoadPct, setTowerLoadPct] = useState(75);
  const [monitorName, setMonitorName] = useState((user as any)?.email?.split('@')[0] ?? '');
  const [monitorSig, setMonitorSig] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverSig, setDriverSig] = useState('');
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState('');

  const zoneTickets = useMemo(
    () => loadTickets.filter(t => t.zoneId === String(zone.id)),
    [loadTickets, zone.id],
  );
  const inTransitCount = zoneTickets.filter(t => t.status === 'in_transit' || t.status === 'at_site').length;

  const filtered = useMemo(() => {
    let list = statusFilter === 'all'
      ? zoneTickets
      : zoneTickets.filter(t => t.status === 'in_transit' || t.status === 'at_site');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.ticketNumber || '').toLowerCase().includes(q) ||
        (t.truckNumber || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (b.pickupTime || '').localeCompare(a.pickupTime || '')).slice(0, 50);
  }, [zoneTickets, statusFilter, search]);

  const handleScan = useCallback((decoded: string) => {
    const match = decoded.match(/\/debris\/verify\/(\d+)/);
    if (!match) { setScanError('Not a debris ticket QR code.'); return; }
    const ticketId = parseInt(match[1]);
    const found = loadTickets.find(t => t.id === ticketId);
    if (found) {
      setSelected(found);
      setTowerLoadPct(parseFloat(found.loadPercentage) || 75);
      setShowScanner(false);
      setScanError('');
    } else {
      setScanError(`Ticket #${ticketId} not found in this zone.`);
    }
  }, [loadTickets]);

  const handleVerify = async (approved: boolean) => {
    if (!selected) return;
    if (!monitorName.trim()) { notifications.show({ message: 'Enter monitor name', color: 'var(--color-error)' }); return; }
    if (approved && !monitorSig) { notifications.show({ message: 'Tower monitor signature required', color: 'var(--color-error)' }); return; }
    if (approved && !driverSig) { notifications.show({ message: 'Driver delivery signature required', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      await updateLoadTicket(selected.id, {
        towerLoadPct: approved ? String(towerLoadPct) : selected.loadingLoadPct || selected.loadPercentage,
        towerMonitorName: monitorName.trim(),
        monitorName: monitorName.trim(),
        towerMonitorSignature: approved ? monitorSig : '',
        driverDeliverySignature: approved ? driverSig : '',
        status: approved ? 'verified' : 'voided',
        disposalTime: new Date().toISOString(),
      } as any);
      notifications.show({
        message: approved ? `Ticket ${selected.ticketNumber || `#${selected.id}`} verified` : 'Ticket voided',
        color: approved ? 'var(--color-success)' : 'var(--color-error)',
      });
      setSelected(null);
      setMonitorSig('');
      setDriverSig('');
    } catch {
      notifications.show({ message: 'Update failed', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  const statusColor: Record<string, string> = {
    in_transit: '#d97706', at_site: '#7c3aed', delivered: '#059669',
    verified: '#059669', draft: '#939598', voided: '#B12525',
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
        background: '#fef3c7', borderRadius: 4, padding: '8px 16px',
      }}>
        <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
          🏗️ Tower Monitor · {zone.zoneName || zone.zoneCode}
        </span>
        {inTransitCount > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#d97706', fontWeight: 700 }}>
            {inTransitCount} en route
          </span>
        )}
      </div>

      {/* Scan QR — primary action */}
      {!showScanner && (
        <button
          onClick={() => { setShowScanner(true); setScanError(''); }}
          style={{
            width: '100%', padding: '14px', background: '#1e293b', color: '#fff',
            border: 'none', borderRadius: 4, fontSize: 16, fontWeight: 800,
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginBottom: 16,
          }}>
          <IconQrcode size={22} /> Scan Driver QR Code
        </button>
      )}

      {showScanner && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ background: '#1e293b', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <QRScanner onScan={handleScan} active={showScanner} />
          </div>
          {scanError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 16px', fontSize: 12, color: '#B12525', display: 'flex', gap: 8, marginBottom: 8 }}>
              <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              {scanError}
            </div>
          )}
          <button onClick={() => { setShowScanner(false); setScanError(''); }} className="sp-btn sp-btn-secondary" style={{ width: '100%' }}>
            Cancel Scan
          </button>
        </div>
      )}

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['in_transit', 'all'] as const).map(f => (
          <button key={f} onClick={() => setStatusFilter(f)} style={{
            flex: 1, padding: '8px 4px', border: '1.5px solid', borderRadius: 4,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            background: statusFilter === f ? '#92400e' : 'white',
            color: statusFilter === f ? 'white' : '#475569',
            borderColor: statusFilter === f ? '#92400e' : '#e2e8f0',
          }}>
            {f === 'in_transit' ? 'En Route' : 'All'}
            {f === 'in_transit' && inTransitCount > 0 && (
              <span style={{ marginLeft: 4, background: statusFilter === f ? 'rgba(255,255,255,0.25)' : '#fef3c7', color: statusFilter === f ? 'white' : '#92400e', padding: '0 5px', borderRadius: 99, fontSize: 12 }}>
                {inTransitCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <IconSearch size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
        <input className="sp-input" style={{ paddingLeft: 32, fontSize: 14 }}
          placeholder="Ticket # or truck…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Ticket list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#939598', fontSize: 14, padding: '40px 0' }}>
          {statusFilter === 'in_transit' ? 'No trucks currently en route.' : 'No tickets found.'}
        </div>
      ) : filtered.map(t => {
        const color = DEBRIS_TYPE_COLORS[t.debrisType] || '#7B7D7F';
        const sc = statusColor[t.status] || '#939598';
        return (
          <div key={t.id}
            onClick={() => { setSelected(t); setTowerLoadPct(parseFloat(t.loadPercentage) || 75); setMonitorSig(''); setDriverSig(''); }}
            style={{
              background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4,
              padding: '16px 16px', marginBottom: 8, cursor: 'pointer',
              borderLeft: `4px solid ${color}`,
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C' }}>
                  {t.ticketNumber || `#${t.id}`}
                  {t.truckNumber && <span style={{ fontSize: 12, color: '#7B7D7F', fontWeight: 400 }}> · Truck {t.truckNumber}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                  {DEBRIS_TYPE_LABELS[t.debrisType] || t.debrisType}
                  {t.loadPercentage ? ` · Driver: ${t.loadPercentage}%` : ''}
                  {t.calculatedCubicYards ? ` · ${parseFloat(t.calculatedCubicYards).toFixed(1)} CY` : ''}
                </div>
                {t.loadingMonitorName && (
                  <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 2 }}>✓ Loading monitor: {t.loadingMonitorName}</div>
                )}
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: sc, background: `${sc}18`, padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                {t.status?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        );
      })}

      {/* ── Verify drawer ── */}
      {selected && (
        <div className="sp-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="sp-drawer" onClick={e => e.stopPropagation()} style={{ paddingBottom: 100 }}>
            <div className="sp-drawer-handle" />
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 4 }}>
              {selected.ticketNumber || `Ticket #${selected.id}`}
            </div>
            <div style={{ fontSize: 14, color: '#7B7D7F', marginBottom: 16 }}>
              Truck {selected.truckNumber || '—'} · {DEBRIS_TYPE_LABELS[selected.debrisType] || selected.debrisType}
            </div>

            {/* Driver's report */}
            <div style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Driver's Report</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#d97706' }}>
                {selected.loadPercentage ? `${selected.loadPercentage}% load` : '—'}
              </div>
              {selected.calculatedCubicYards && parseFloat(selected.calculatedCubicYards) > 0 && (
                <div style={{ fontSize: 12, color: '#939598', marginTop: 2 }}>
                  = {parseFloat(selected.calculatedCubicYards).toFixed(1)} CY calculated
                </div>
              )}
              {selected.unitCount && <div style={{ fontSize: 12, color: '#939598' }}>{selected.unitCount} units</div>}
              {selected.loadingMonitorName && (
                <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 4 }}>
                  ✓ Loading monitor: {selected.loadingMonitorName} · {selected.loadingLoadPct ? `${selected.loadingLoadPct}%` : ''}
                </div>
              )}
            </div>

            {/* Stops summary */}
            {(() => {
              const stops = parseStops(selected.pickupStopsJson);
              return stops.length > 0 ? (
                <div style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
                    {stops.length} Stop{stops.length !== 1 ? 's' : ''}
                  </div>
                  {stops.map((s: any, i: number) => (
                    <div key={i} style={{ fontSize: 12, color: '#7B7D7F', padding: '3px 0', borderBottom: i < stops.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                      {s.address || 'No address'}
                      {s.estimatedLoadPercent ? ` · ${s.estimatedLoadPercent}%` : ''}
                    </div>
                  ))}
                </div>
              ) : null;
            })()}

            {/* Load estimates comparison */}
            {(() => {
              const roadPct   = parseFloat(selected.loadingLoadPct || '0');
              const driverPct = parseFloat(selected.loadPercentage || '0');
              const diff = roadPct > 0 ? Math.abs(towerLoadPct - roadPct) : 0;
              const hasMismatch = diff >= 15;
              return (
                <>
                  {/* Comparison bars when we have prior estimates */}
                  {(roadPct > 0 || driverPct > 0) && (
                    <div style={{ background: '#F8F8F8', border: '1px solid #e2e8f0', borderRadius: 4, padding: '16px 16px', marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Load Estimates
                      </div>
                      {driverPct > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginBottom: 3 }}>
                            <span>🚛 Driver</span>
                            <strong>{driverPct}%</strong>
                          </div>
                          <div style={{ height: 8, background: '#e2e8f0', borderRadius: 99 }}>
                            <div style={{ width: `${driverPct}%`, height: '100%', background: '#d97706', borderRadius: 99 }} />
                          </div>
                        </div>
                      )}
                      {roadPct > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginBottom: 3 }}>
                            <span>🦺 Road Monitor ({selected.loadingMonitorName || 'Unknown'})</span>
                            <strong>{roadPct}%</strong>
                          </div>
                          <div style={{ height: 8, background: '#e2e8f0', borderRadius: 99 }}>
                            <div style={{ width: `${roadPct}%`, height: '100%', background: '#7c3aed', borderRadius: 99 }} />
                          </div>
                        </div>
                      )}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#2563eb', fontWeight: 700, marginBottom: 3 }}>
                          <span>🏗️ You (Tower)</span>
                          <strong>{towerLoadPct}%</strong>
                        </div>
                        <div style={{ height: 8, background: '#e2e8f0', borderRadius: 99 }}>
                          <div style={{ width: `${towerLoadPct}%`, height: '100%', background: '#2563eb', borderRadius: 99 }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mismatch warning */}
                  {hasMismatch && (
                    <div style={{
                      background: '#fef2f2', border: '2px solid #B12525', borderRadius: 4,
                      padding: '16px 16px', marginBottom: 16,
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                    }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#B12525', marginBottom: 4 }}>
                          Load Mismatch — {diff}% difference
                        </div>
                        <div style={{ fontSize: 14, color: '#7f1d1d', lineHeight: 1.5 }}>
                          Road monitor recorded <strong>{roadPct}%</strong>, you're seeing <strong>{towerLoadPct}%</strong>.
                          Investigate before verifying — this may indicate a partial dump or discrepancy.
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#B12525', fontWeight: 600 }}>
                          Adjust your estimate or void the ticket if load cannot be verified.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tower load estimate slider */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
                      Your Load Estimate: <strong style={{ color: hasMismatch ? '#B12525' : '#1B1C1C' }}>{towerLoadPct}%</strong>
                    </label>
                    <input type="range" min={5} max={100} step={5} value={towerLoadPct}
                      onChange={e => setTowerLoadPct(Number(e.target.value))} style={{ width: '100%' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#939598', marginTop: 2 }}>
                      <span>5%</span><span>100%</span>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Monitor name */}
            <div className="sp-field">
              <label className="sp-label">Tower Monitor Name / ID <span className="sp-req">*</span></label>
              <input className="sp-input" value={monitorName} onChange={e => setMonitorName(e.target.value)} placeholder="Your name or badge ID" />
            </div>

            {/* Tower monitor signature */}
            <SignaturePad
              label="Tower Monitor Signature *"
              onCapture={setMonitorSig}
            />

            {/* Divider */}
            <div style={{ borderTop: '1px solid #e2e8f0', margin: '8px 0 16px', paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Driver Sign-off at Disposal</div>
            </div>

            {/* Driver name */}
            <div className="sp-field">
              <label className="sp-label">Driver Name</label>
              <input className="sp-input" value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="Driver's name" />
            </div>

            {/* Driver delivery signature */}
            <SignaturePad
              label="Driver Delivery Signature *"
              onCapture={setDriverSig}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sp-btn sp-btn-secondary sp-btn-sm" style={{ flex: 1 }} onClick={() => setSelected(null)}>
                Cancel
              </button>
              <button
                style={{
                  flex: 1, padding: '8px 0', background: '#B12525', color: '#fff',
                  border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: saving ? 0.6 : 1,
                }}
                onClick={() => handleVerify(false)} disabled={saving}>
                <IconX size={16} /> Void
              </button>
              <button
                className="sp-btn sp-btn-primary sp-btn-sm"
                style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                onClick={() => handleVerify(true)} disabled={saving}>
                <IconCheck size={16} /> {saving ? 'Saving…' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Monitor Tab ──────────────────────────────────────────

export default function DebrisPortalMonitor({ zone, role }: Props) {
  if (role === 'road_monitor') return <RoadMonitorView zone={zone} />;
  return <TowerMonitorView zone={zone} />;
}
